import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X, Plus, CloudUpload, FileText, Bookmark, Check } from 'lucide-react'
import { Button } from './Button'
import { Dropdown } from './Dropdown'
import { showToastMessage } from './Toast'
import { getScheduledWeekOptions, getDefaultScheduledWeek } from '../features/academics/create-lesson-plan/utils/planWeeksUtils'

export interface TopicFormData {
  id: string
  name: string
  scheduledWeek: string
  pedagogicalNotes: string
  attachments: Array<{ fileName: string; size: string; subtitle?: string; file?: File; stagingKey?: string }>
  status?: 'draft' | 'scheduled'
}

export interface AddTopicModalProps {
  open: boolean
  onClose: () => void
  onAddTopics: (topics: TopicFormData[]) => Promise<void> | void
  onSaveDraftTopic?: (topic: TopicFormData) => Promise<void> | void
  chapterNum?: number
  chapterTitle?: string
  sectionNumber?: number | string
  totalWeeks?: number
  isSubmitting?: boolean
  existingChapters?: Array<{ topics?: Array<{ scheduledWeek?: string | number | null }> }>
}

export function AddTopicModal({
  open,
  onClose,
  onAddTopics,
  onSaveDraftTopic,
  chapterNum = 2,
  chapterTitle = 'Plant Reproductive System',
  sectionNumber = 2,
  totalWeeks = 16,
  isSubmitting = false,
  existingChapters = [],
}: AddTopicModalProps) {
  const initialDefaultWeek = useMemo(() => {
    return getDefaultScheduledWeek(existingChapters, Math.max(0, chapterNum - 1), totalWeeks, false)
  }, [existingChapters, chapterNum, totalWeeks])

  const [topicForms, setTopicForms] = useState<TopicFormData[]>([
    {
      id: `topic-${Date.now()}-1`,
      name: '',
      scheduledWeek: initialDefaultWeek,
      pedagogicalNotes: '',
      attachments: [],
      status: 'scheduled',
    },
  ])

  if (!open) return null

  const handleUpdateFormField = (
    idx: number,
    field: keyof TopicFormData,
    value: any
  ) => {
    setTopicForms((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, [field]: value } : item))
    )
  }

  const handleFileChange = (
    formIdx: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const newAtts = Array.from(files).map((f) => ({
      fileName: f.name,
      size: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
      subtitle: f.type || 'Resource File',
      file: f,
    }))
    setTopicForms((prev) =>
      prev.map((item, i) =>
        i === formIdx
          ? {
              ...item,
              attachments: [...item.attachments, ...newAtts],
            }
          : item
      )
    )
    showToastMessage(`${files.length} resource(s) added`, 'success')
  }

  const handleRemoveAttachment = (formIdx: number, attIdx: number) => {
    setTopicForms((prev) =>
      prev.map((item, i) =>
        i === formIdx
          ? {
              ...item,
              attachments: item.attachments.filter((_, aIdx) => aIdx !== attIdx),
            }
          : item
      )
    )
  }

  const handleAddAnotherTopic = () => {
    setTopicForms((prev) => {
      const nextWeek = getDefaultScheduledWeek([{ topics: prev }], 0, totalWeeks, false)
      return [
        ...prev,
        {
          id: `topic-${Date.now()}-${prev.length + 1}`,
          name: '',
          scheduledWeek: nextWeek,
          pedagogicalNotes: '',
          attachments: [],
          status: 'scheduled',
        },
      ]
    })
  }

  const isFullValid = topicForms.every(
    (tf) => tf.name.trim().length > 0
  )

  const isDraftValid = topicForms.some((tf) => tf.name.trim().length > 0)

  const handleSubmit = (asDraft: boolean) => {
    const formatted = topicForms.map((tf) => ({
      ...tf,
      status: asDraft ? ('draft' as const) : ('scheduled' as const),
    }))

    if (asDraft) {
      if (onSaveDraftTopic) {
        onSaveDraftTopic(formatted[0])
      } else {
        onAddTopics(formatted)
      }
    } else {
      onAddTopics(formatted)
    }
    onClose()
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/45 px-3 sm:px-4 backdrop-blur-[6px] py-4 sm:py-6 font-sans overflow-hidden"
      onMouseDown={isSubmitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[760px] max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden border border-neutral-200 flex flex-col font-sans my-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-neutral-100 p-4 sm:p-6 bg-white shrink-0">
          <div className="flex items-start gap-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e6f4f1] text-[#0a5b63]">
              <FileText className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <h2 className="text-base sm:text-lg font-bold text-neutral-900 font-sans">
                Add New Topic
              </h2>
              <p className="text-xs text-neutral-500 font-sans">
                Add a curriculum topic and assign it to an active chapter in the syllabus schedule.
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 space-y-6 flex-1 min-h-0 overflow-y-auto bg-white">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0a5b63] text-white text-xs font-bold font-sans">
                {sectionNumber}
              </div>
              <span className="text-xs font-bold text-neutral-900 font-sans">
                Topic Information & Schedule
              </span>
            </div>
            <span className="text-xs font-semibold text-neutral-400 font-sans">
              Context: Chapter {chapterNum}
            </span>
          </div>

          {/* Repeatable Topic Forms */}
          {topicForms.map((tf, formIdx) => (
            <div
              key={tf.id}
              className="space-y-4 p-4 rounded-xl border border-neutral-200/80 bg-white"
            >
              {/* Row 1: Topic Title & Index + Scheduled Week */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-8 space-y-1.5">
                  <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
                    Topic Title & Index
                  </label>
                  <input
                    type="text"
                    value={tf.name}
                    onChange={(e) =>
                      handleUpdateFormField(formIdx, 'name', e.target.value)
                    }
                    placeholder="2.1 Flower Anatomy & Pollination Mechanisms"
                    className="w-full h-10 rounded-lg border border-neutral-200 bg-white px-3 text-xs font-medium text-neutral-900 focus:border-[#0a5b63] focus:outline-none font-sans"
                  />
                </div>

                <div className="md:col-span-4 space-y-1.5">
                  <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
                    Scheduled Week
                  </label>
                  <Dropdown
                    disabled={isSubmitting}
                    options={getScheduledWeekOptions(totalWeeks, tf.scheduledWeek, false)}
                    value={tf.scheduledWeek}
                    onChange={(val) =>
                      handleUpdateFormField(formIdx, 'scheduledWeek', val)
                    }
                    placeholder="Select Week"
                    className="w-full"
                  />
                </div>
              </div>

              {/* Row 2: Pedagogical Notes & Lab Directives */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
                  Pedagogical Notes & Lab Directives
                </label>
                <textarea
                  rows={3}
                  value={tf.pedagogicalNotes}
                  onChange={(e) =>
                    handleUpdateFormField(
                      formIdx,
                      'pedagogicalNotes',
                      e.target.value
                    )
                  }
                  placeholder="Microscope lab with hibiscus flowers, observing stamen and pistil structure under 40x magnification. Students will illustrate pollen grains and map cross-pollination vector pathways."
                  className="w-full rounded-lg border border-neutral-200 bg-white p-3 text-xs text-neutral-800 focus:border-[#0a5b63] focus:outline-none font-sans resize-none leading-relaxed"
                />
              </div>

              {/* Row 3: Attached Curricular Resources */}
              <div className="space-y-2">
                <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
                  Attached Curricular Resources
                </label>

                <div className="flex flex-wrap items-center gap-3">
                  {tf.attachments.map((att, attIdx) => {
                    const displayName = att.fileName || att.file?.name || 'Attachment'
                    return (
                      <div
                        key={attIdx}
                        title={displayName}
                        className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2.5 shadow-2xs text-xs font-sans max-w-xs"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[#e6f4f1] text-[#0a5b63]">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-bold text-neutral-900 truncate" title={displayName}>
                            {displayName}
                          </p>
                          <p className="text-[11px] text-neutral-400 truncate">
                            {att.size ? att.size : ''} {att.subtitle ? `• ${att.subtitle}` : ''}
                          </p>
                        </div>
                        {!isSubmitting && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAttachment(formIdx, attIdx)}
                            className="text-neutral-400 hover:text-neutral-600 p-1"
                            title="Remove attachment"
                            aria-label="Remove attachment"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )
                  })}

                  {!isSubmitting && (
                    <label className="flex-1 min-w-[220px] h-14 rounded-lg border border-dashed border-neutral-300 bg-neutral-50/80 hover:bg-neutral-100/60 hover:border-neutral-400 transition flex items-center justify-center gap-2 text-xs font-bold text-[#0a5b63] font-sans cursor-pointer">
                      <CloudUpload className="h-4 w-4" />
                      <span>+ Attach File(s)</span>
                      <input type="file" multiple className="hidden" onChange={(e) => handleFileChange(formIdx, e)} />
                    </label>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Add Another Topic Button */}
          <button
            type="button"
            onClick={handleAddAnotherTopic}
            className="w-full py-3 rounded-xl border border-dashed border-neutral-200 bg-neutral-50/60 hover:bg-neutral-100/60 text-xs font-bold text-[#0a5b63] transition flex items-center justify-center gap-1.5 font-sans"
          >
            <Plus className="h-4 w-4" />
            Add Another Topic to Chapter {chapterNum}
          </button>
        </div>

        {/* Modal Footer — 3 Actions */}
        <div className="flex items-center justify-between border-t border-neutral-200 bg-white p-4 shrink-0">
          <Button
            variant="secondary"
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-10 px-5 text-xs font-bold border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-60 disabled:bg-white disabled:text-neutral-700"
          >
            Cancel
          </Button>

          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              type="button"
              isLoading={isSubmitting}
              disabled={!isDraftValid || isSubmitting}
              onClick={() => handleSubmit(true)}
              className="h-10 px-4 text-xs font-bold border border-neutral-300 text-[#0a5b63] bg-white hover:bg-neutral-50 inline-flex items-center gap-1.5 disabled:opacity-60 disabled:bg-white disabled:text-[#0a5b63]"
            >
              {!isSubmitting && <Bookmark className="h-4 w-4" />}
              {isSubmitting ? 'Saving...' : 'Save as Draft Topic'}
            </Button>

            <Button
              type="button"
              isLoading={isSubmitting}
              disabled={!isFullValid || isSubmitting}
              onClick={() => handleSubmit(false)}
              className="h-10 px-5 text-xs font-bold bg-[#0a5b63] hover:bg-[#08484f] text-white inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:bg-[#0a5b63] disabled:text-white"
            >
              {!isSubmitting && <Check className="h-4 w-4" />}
              {isSubmitting ? 'Adding...' : `Add Topic to Chapter ${chapterNum}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document === 'undefined' ? modalContent : createPortal(modalContent, document.body)
}

export default AddTopicModal
